import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import { httpClient } from '../../common/httpclient';
import { KitButton, KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import { KitSeg, KitSwitch } from '../common/MobileKitExtras';
import MobileSheet from '../common/MobileSheet';
import { ProtoIcon, protoIcon } from '../common/ProtoIcon';

/**
 * Início mobile — `prototipos-mobile/principal/home.html`: cartão degradê "Vendas de
 * hoje", Atalhos, Resumo do dia (KPIs), Ordens em andamento, Últimas vendas e
 * Próximos serviços. Os dados vêm do `Home.js` (mesmas chamadas da versão desktop).
 */

const Bell = protoIcon('bell');
export const HomeBellIcon = Bell;
export const HomeCustomizeIcon = protoIcon('sliders');

function Kpi({ label, icon, value, delta, deltaTone, loading }) {
    return (
        <Box sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '16px', p: '14px', boxShadow: '0 1px 2px rgba(15,23,42,.06)', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'text.disabled', fontSize: 11.5, fontWeight: 600 }}>
                <span>{label}</span>
                <ProtoIcon name={icon} size={16} />
            </Box>
            {loading ? (
                <Skeleton variant="text" width={70} sx={{ fontSize: 21, my: '6px' }} />
            ) : (
                <Box sx={{ fontSize: 21, fontWeight: 800, letterSpacing: '-.02em', m: '6px 0 2px', whiteSpace: 'nowrap' }}>{value}</Box>
            )}
            <Box sx={{ fontSize: 11.5, fontWeight: 700, color: deltaTone === 'down' ? 'error.main' : 'success.main' }}>{delta}</Box>
        </Box>
    );
}

function QuickLink({ icon, label, onClick, primary }) {
    return (
        <Box component="button" type="button" onClick={onClick} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', fontSize: 11.5, fontWeight: 600, textAlign: 'center', color: 'text.primary', border: 0, background: 'none', p: 0, fontFamily: 'inherit', cursor: 'pointer', lineHeight: 1.4 }}>
            <Box
                component="span"
                sx={(t) => ({
                    width: 52, height: 52, borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06)',
                    ...(primary
                        ? { backgroundImage: t.palette.gradient.brand, color: '#fff', border: 0 }
                        : { backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', color: 'primary.main' }),
                })}
            >
                <ProtoIcon name={icon} size={24} />
            </Box>
            {label}
        </Box>
    );
}

// Resumo do dia editável: até KPI_MAX indicadores visíveis, no mínimo KPI_MIN.
const KPI_MAX = 4;
const KPI_MIN = 1;
// Padrão = os 4 que a tela mostrava antes da edição.
const KPI_DEFAULT = ['os', 'quotes', 'receivable', 'stock'];
const KPI_DESCRIPTIONS = {
    os: 'Ordens não iniciadas e em execução',
    os_billed: 'Valor faturado hoje',
    os_late: 'Abertas com prazo de entrega vencido (últimos 30 dias)',
    products_sold: 'Total de itens vendidos hoje',
    receivable: 'Títulos pendentes com vencimento hoje',
    payable: 'Contas pendentes com vencimento hoje',
    stock: 'Produtos abaixo do estoque mínimo',
    quotes_total: 'Total de orçamentos no mês',
    quotes: 'Orçamentos aguardando aprovação no mês',
};

function readKpiSelection(storageKey) {
    if (!storageKey) return null;
    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey));
        return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : null;
    } catch (e) {
        return null;
    }
}

function writeKpiSelection(storageKey, ids) {
    if (!storageKey) return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch (e) {
        // armazenamento indisponível: a escolha vale só até recarregar
    }
}

// Layout da Início (personalização): cartão principal exclusivo + de 1 a SECTION_MAX seções.
// Guardado em localStorage como `{ hero: 'sales'|'orders', sections: [ids na ordem de ativação] }`.
const SECTION_MAX = 4;
const SECTION_MIN = 1;
// Padrão = as seções que a tela mostrava antes da personalização.
const SECTION_DEFAULT = ['orders_open', 'sales_recent', 'upcoming'];
const SECTION_CATALOG = [
    { id: 'orders_open', label: 'Ordens em andamento', description: 'Ordens não iniciadas e em execução' },
    { id: 'orders_recent', label: 'Últimas ordens', description: 'Ordens mais recentes, de qualquer status' },
    { id: 'sales_recent', label: 'Últimas vendas', description: 'Vendas mais recentes dos últimos 30 dias' },
    { id: 'upcoming', label: 'Próximos serviços', description: 'Serviço recorrente de veículo prestes a vencer' },
    { id: 'quotes_recent', label: 'Últimos orçamentos', description: 'Orçamentos mais recentes dos últimos 30 dias' },
    { id: 'products_sold', label: 'Produtos vendidos', description: 'Produtos mais vendidos no mês' },
    { id: 'products_low', label: 'Produtos com baixo estoque', description: 'Produtos no estoque mínimo ou abaixo dele' },
];
const HERO_OPTIONS = [
    { value: 'sales', label: 'Vendas hoje' },
    { value: 'orders', label: 'Ordens hoje' },
];

function readLayout(storageKey) {
    if (!storageKey) return null;
    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey));
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
        return {
            hero: typeof parsed.hero === 'string' ? parsed.hero : null,
            sections: Array.isArray(parsed.sections) ? parsed.sections.filter((id) => typeof id === 'string') : null,
        };
    } catch (e) {
        return null;
    }
}

function writeLayout(storageKey, layout) {
    if (!storageKey) return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(layout));
    } catch (e) {
        // armazenamento indisponível: a escolha vale só até recarregar
    }
}

const QuoteIcon = protoIcon('quote');

const quoteTone = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.startsWith('aprovado')) return 'success';
    if (s.startsWith('aguardando')) return 'warning';
    if (s.startsWith('ordem')) return 'info';
    return undefined;
};

const orderTone =(status) => {
    if (status === 'Em Execução') return { tone: '', pill: 'info' };
    if (status === 'Não Iniciada') return { tone: 'amber', pill: 'warning' };
    return { tone: 'green', pill: 'success' };
};

export default function MobileHome({
    permissions, sales, orders, quotes, products, kpis, kpiStorageKey, layoutStorageKey, layoutOpen, onLayoutClose, upcoming, notifications, notifOpen, onNotifClose, shortcuts,
}) {
    const navigate = useNavigate();
    // Geração de OS a partir do cartão "Próximos serviços": confirma e cria a OS no backend.
    const [generateOpen, setGenerateOpen] = React.useState(false);
    const [generating, setGenerating] = React.useState(false);
    const [generateError, setGenerateError] = React.useState('');
    const handleGenerate = async () => {
        const item = upcoming.item;
        if (!item || generating) return;
        setGenerating(true);
        setGenerateError('');
        try {
            const response = await httpClient.post('/orderservices/from-service', {
                customer_id: item.customer_id,
                vehicle_id: item.vehicle_id,
                service_id: item.service_id,
            });
            const id = response.data?.id;
            setGenerateOpen(false);
            if (id) navigate(`/serviceorder/${id}/operator`, { state: { id, fromFlow: true }, replace: true });
        } catch (error) {
            console.error('Erro ao gerar ordem de serviço:', error);
            const data = error.response?.data;
            setGenerateError((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao gerar a ordem de serviço');
        } finally {
            setGenerating(false);
        }
    };
    const [hidden, setHidden] = React.useState(false);
    const [kpiEditOpen, setKpiEditOpen] = React.useState(false);
    const [kpiDraft, setKpiDraft] = React.useState([]);
    const [kpiSaved, setKpiSaved] = React.useState(() => readKpiSelection(kpiStorageKey));
    // A chave só existe depois que o usuário carrega; relê quando ela muda.
    React.useEffect(() => { setKpiSaved(readKpiSelection(kpiStorageKey)); }, [kpiStorageKey]);

    // Seleção efetiva: salva (ou padrão), só ids disponíveis (permissão), na ordem escolhida, até KPI_MAX.
    const availableIds = kpis.map((k) => k.id);
    const resolveSelection = (ids) => ids.filter((id, i) => availableIds.includes(id) && ids.indexOf(id) === i).slice(0, KPI_MAX);
    let selectedIds = resolveSelection(kpiSaved || KPI_DEFAULT);
    if (selectedIds.length === 0) selectedIds = resolveSelection(KPI_DEFAULT);
    if (selectedIds.length === 0) selectedIds = availableIds.slice(0, KPI_MAX);
    const visibleKpis = selectedIds.map((id) => kpis.find((k) => k.id === id));

    const openKpiEdit = () => { setKpiDraft(selectedIds); setKpiEditOpen(true); };
    const toggleKpi = (id) => {
        setKpiDraft((prev) => {
            if (prev.includes(id)) return prev.length <= KPI_MIN ? prev : prev.filter((x) => x !== id);
            return prev.length >= KPI_MAX ? prev : [...prev, id];
        });
    };
    const saveKpis = () => {
        writeKpiSelection(kpiStorageKey, kpiDraft);
        setKpiSaved(kpiDraft);
        setKpiEditOpen(false);
    };
    // Layout (cartão principal + seções): salvo, validado contra as permissões atuais.
    const [layoutSaved, setLayoutSaved] = React.useState(() => readLayout(layoutStorageKey));
    React.useEffect(() => { setLayoutSaved(readLayout(layoutStorageKey)); }, [layoutStorageKey]);
    const [layoutDraft, setLayoutDraft] = React.useState({ hero: null, sections: [] });

    const sectionAvailable = {
        orders_open: permissions.orders,
        orders_recent: permissions.orders,
        sales_recent: permissions.sales,
        upcoming: upcoming.enabled,
        quotes_recent: permissions.quotes,
        products_sold: permissions.products,
        products_low: permissions.products,
    };
    const availableSections = SECTION_CATALOG.filter((s) => sectionAvailable[s.id]);
    const availableHeroes = HERO_OPTIONS.filter((h) => (h.value === 'sales' ? permissions.sales : permissions.orders));
    const resolveSections = (ids) => ids.filter((id, i) => sectionAvailable[id] && ids.indexOf(id) === i).slice(0, SECTION_MAX);
    const defaultLayout = () => {
        let sections = resolveSections(SECTION_DEFAULT);
        if (sections.length === 0) sections = availableSections.slice(0, SECTION_MAX).map((s) => s.id);
        // Sem permissão de vendas o padrão é Ordens hoje (e vice-versa).
        return { hero: availableHeroes.length ? availableHeroes[0].value : null, sections };
    };
    const resolveLayout = () => {
        const def = defaultLayout();
        const hero = layoutSaved && availableHeroes.some((h) => h.value === layoutSaved.hero) ? layoutSaved.hero : def.hero;
        const savedSections = layoutSaved && layoutSaved.sections ? resolveSections(layoutSaved.sections) : [];
        return { hero, sections: savedSections.length ? savedSections : def.sections };
    };
    const layout = resolveLayout();

    const openLayoutEdit = () => setLayoutDraft(resolveLayout());
    // A folha é aberta pelo Home (ícone do topo); o rascunho parte da seleção efetiva a cada abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => { if (layoutOpen) openLayoutEdit(); }, [layoutOpen]);
    const toggleSection = (id) => {
        setLayoutDraft((prev) => {
            if (prev.sections.includes(id)) return prev.sections.length <= SECTION_MIN ? prev : { ...prev, sections: prev.sections.filter((x) => x !== id) };
            return prev.sections.length >= SECTION_MAX ? prev : { ...prev, sections: [...prev.sections, id] };
        });
    };
    const saveLayout = () => {
        const next = { hero: layoutDraft.hero, sections: layoutDraft.sections };
        writeLayout(layoutStorageKey, next);
        setLayoutSaved(next);
        onLayoutClose();
    };

    const Car = protoIcon('car');
    const Receipt = protoIcon('receipt');
    const Alert = protoIcon('alert');
    const Package = protoIcon('package');
    const money = (n) => `R$ ${(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    // Cartão principal: "Vendas de hoje" (como era) ou "Ordens de hoje" (mesmo visual, dados de OS).
    const heroIsOrders = layout.hero === 'orders';
    const heroData = heroIsOrders
        ? { label: 'Ordens de hoje', src: orders, one: 'ordem', many: 'ordens', dash: permissions.ordersDashboard, dashPath: '/dashboards/order', list: permissions.orders, listPath: '/serviceorders', listLabel: 'Minhas ordens' }
        : { label: 'Vendas de hoje', src: sales, one: 'venda', many: 'vendas', dash: permissions.dashboard, dashPath: '/dashboards/sale', list: permissions.salesList, listPath: '/sales', listLabel: 'Minhas vendas' };
    const deltaText = heroData.src.deltaPct === null
        ? null
        : `${heroData.src.deltaPct >= 0 ? '+' : '-'}${Math.abs(heroData.src.deltaPct).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% vs. ontem`;
    const heroSub = [deltaText, heroData.src.countToday !== null ? `${heroData.src.countToday} ${heroData.src.countToday === 1 ? heroData.one : heroData.many}` : null].filter(Boolean).join(' · ');

    const emptyCard = (text) => (
        <KitCard sx={{ textAlign: 'center', color: 'text.disabled', fontSize: 13 }}>{text}</KitCard>
    );
    const orderItem = (o, withValue) => {
        const { tone, pill } = orderTone(o.status);
        return (
            <KitItem
                key={o.order_service_id}
                icon={Car}
                tone={tone}
                title={`OS #${o.number}`}
                sub={[o.customer_name, withValue ? `R$ ${o.amount}` : (o.count_items ? `${o.count_items} ${o.count_items === 1 ? 'item' : 'itens'}` : null)].filter(Boolean).join(' · ')}
                end={<KitPill tone={pill}>{o.status}</KitPill>}
                onClick={() => navigate(`/serviceorders/${o.order_service_id}/view`)}
            />
        );
    };
    // Cada seção do catálogo; a ordem de exibição vem de `layout.sections` (ordem de ativação).
    const renderSection = (id) => {
        switch (id) {
            case 'orders_open':
                return (
                    <>
                        <SecH title="Ordens em andamento" linkLabel="Ver todas" onLink={() => navigate('/serviceorders')} />
                        {orders.items.length > 0 ? <KitList>{orders.items.map((o) => orderItem(o, false))}</KitList> : emptyCard(orders.loading ? 'Carregando...' : 'Nenhuma ordem em andamento')}
                    </>
                );
            case 'orders_recent':
                return (
                    <>
                        <SecH title="Últimas ordens" linkLabel="Ver todas" onLink={() => navigate('/serviceorders')} />
                        {orders.recent.length > 0 ? <KitList>{orders.recent.map((o) => orderItem(o, true))}</KitList> : emptyCard(orders.loading ? 'Carregando...' : 'Nenhuma ordem nos últimos 30 dias')}
                    </>
                );
            case 'sales_recent':
                return (
                    <>
                        <SecH title="Últimas vendas" linkLabel={permissions.salesList ? 'Ver todas' : undefined} onLink={() => navigate('/sales')} />
                        {sales.recent.length > 0 ? (
                            <KitList>
                                {sales.recent.map((s) => (
                                    <KitItem
                                        key={s.id}
                                        icon={Receipt}
                                        tone={s.status === 'Cancelada' ? 'red' : 'green'}
                                        title={`Venda #${s.number}`}
                                        sub={[s.customer_name || 'Balcão', (s.created_date || '').split(' ')[1]].filter(Boolean).join(' · ')}
                                        end={<Box sx={{ fontWeight: 800, fontSize: 14 }}>R$ {s.final_total}</Box>}
                                        onClick={() => navigate(`/sales/${s.id}`)}
                                    />
                                ))}
                            </KitList>
                        ) : emptyCard(sales.loading ? 'Carregando...' : 'Nenhuma venda nos últimos 30 dias')}
                    </>
                );
            case 'upcoming':
                return (
                    <>
                        <SecH title="Próximos serviços" linkLabel="Ver todos" onLink={() => navigate('/serviceorders/upcoming')} />
                        <KitCard>
                            {upcoming.item ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Box component="span" sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'warning.bg', color: 'warning.main', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>!</Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ fontWeight: 700 }}>{[upcoming.item.service_name, [upcoming.item.vehicle_model, upcoming.item.license_plate].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</Box>
                                        <Box sx={{ fontSize: 12.5, color: 'text.disabled' }}>{[upcoming.item.when, upcoming.item.customer_name].filter(Boolean).join(' · ')}</Box>
                                    </Box>
                                    {upcoming.canGenerate && <KitButton variant="soft" size="sm" onClick={() => { setGenerateError(''); setGenerateOpen(true); }}>Gerar OS</KitButton>}
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', color: 'text.disabled', fontSize: 13 }}>{upcoming.loading ? 'Carregando...' : 'Nenhum serviço a vencer'}</Box>
                            )}
                        </KitCard>
                    </>
                );
            case 'quotes_recent':
                return (
                    <>
                        <SecH title="Últimos orçamentos" linkLabel="Ver todos" onLink={() => navigate('/servicequotes')} />
                        {quotes.items.length > 0 ? (
                            <KitList>
                                {quotes.items.map((q) => (
                                    <KitItem
                                        key={q.service_quote_id}
                                        icon={QuoteIcon}
                                        title={`Orçamento #${q.number}`}
                                        sub={q.customer_name || undefined}
                                        end={<KitPill tone={quoteTone(q.status)}>{q.status}</KitPill>}
                                        onClick={() => navigate(`/servicequotes/${q.service_quote_id}/view`)}
                                    />
                                ))}
                            </KitList>
                        ) : emptyCard(quotes.loading ? 'Carregando...' : 'Nenhum orçamento nos últimos 30 dias')}
                    </>
                );
            case 'products_sold':
                return (
                    <>
                        <SecH title="Produtos vendidos" linkLabel={permissions.productsReport ? 'Ver todos' : undefined} onLink={() => navigate('/reports/products')} />
                        {products.topSold.length > 0 ? (
                            <KitList>
                                {products.topSold.map((p, i) => (
                                    <KitItem
                                        key={`${p.name}-${i}`}
                                        icon={Package}
                                        tone="green"
                                        title={p.name}
                                        sub={`${i + 1}º mais vendido no mês`}
                                        end={<Box sx={{ fontWeight: 800, fontSize: 14 }}>{p.quantity.toLocaleString('pt-BR')} un.</Box>}
                                    />
                                ))}
                            </KitList>
                        ) : emptyCard(products.loading ? 'Carregando...' : 'Nenhum produto vendido no mês')}
                    </>
                );
            case 'products_low':
                return (
                    <>
                        <SecH title="Produtos com baixo estoque" linkLabel="Ver todos" onLink={() => navigate('/products')} />
                        {products.lowStock.length > 0 ? (
                            <KitList>
                                {products.lowStock.map((p, i) => (
                                    <KitItem
                                        key={`${p.name}-${i}`}
                                        icon={Package}
                                        tone={p.stock <= 0 ? 'red' : 'amber'}
                                        title={p.name}
                                        sub={products.lowStockTotal > products.lowStock.length && i === products.lowStock.length - 1 ? `+${products.lowStockTotal - products.lowStock.length} produtos` : undefined}
                                        end={<KitPill tone={p.stock <= 0 ? 'danger' : 'warning'}>{p.stock} em estoque</KitPill>}
                                        onClick={() => navigate('/products')}
                                    />
                                ))}
                            </KitList>
                        ) : emptyCard(products.loading ? 'Carregando...' : 'Nenhum produto abaixo do estoque mínimo')}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {layout.hero && (
                <Box sx={(t) => ({ backgroundImage: t.palette.gradient.brand, color: '#fff', borderRadius: '16px', p: '16px', boxShadow: t.palette.mode === 'dark' ? '0 6px 20px rgba(0,0,0,.35)' : '0 6px 20px rgba(15,23,42,.08)', mt: '-4px' })}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                        <Box component="span" sx={{ fontSize: 12.5, opacity: 0.85 }}>{heroData.label}</Box>
                        <Box component="button" type="button" aria-label={hidden ? 'Mostrar valor' : 'Ocultar valor'} onClick={() => setHidden((h) => !h)} sx={{ color: '#fff', width: 32, height: 32, borderRadius: '12px', border: 0, background: 'none', p: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                            <ProtoIcon name={hidden ? 'eyeoff' : 'eye'} size={18} />
                        </Box>
                    </Box>
                    <Box sx={{ fontSize: 34, fontWeight: 800, letterSpacing: '-.03em', m: '4px 0 2px' }}>
                        {heroData.src.loading ? <Skeleton variant="text" width={180} sx={{ bgcolor: 'rgba(255,255,255,.3)' }} /> : (hidden ? 'R$ ••••••' : money(heroData.src.today))}
                    </Box>
                    {heroSub && (
                        <Box sx={{ fontSize: 12.5, opacity: 0.9 }}>
                            <Box component="span" sx={{ display: 'inline-block', verticalAlign: '-2px', mr: '4px' }}><ProtoIcon name="trendup" size={14} /></Box>
                            {heroSub}
                        </Box>
                    )}
                    {(heroData.dash || heroData.list) && (
                        <Box sx={{ display: 'flex', mt: '16px', gap: '8px' }}>
                            {heroData.dash && <KitButton variant="glass" size="sm" sx={{ flex: 1 }} onClick={() => navigate(heroData.dashPath)}>Ver dashboard</KitButton>}
                            {heroData.list && <KitButton variant="white" size="sm" sx={{ flex: 1 }} onClick={() => navigate(heroData.listPath)}>{heroData.listLabel}</KitButton>}
                        </Box>
                    )}
                </Box>
            )}

            {/* {shortcuts.length > 0 && (
                <>
                    <SecH title="Atalhos" />
                    <KitCard>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                            {shortcuts.map((s, i) => (
                                <QuickLink key={s.id} icon={s.proto} label={s.short} primary={s.id === 'sale' && i === 0} onClick={() => navigate(s.path)} />
                            ))}
                        </Box>
                    </KitCard>
                </>
            )} */}

            {kpis.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', m: '22px 0 10px' }}>
                        <Typography component="h2" sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.4 }}>Resumo do dia</Typography>
                        <Box component="button" type="button" aria-label="Editar indicadores" onClick={openKpiEdit} sx={{ border: 0, background: 'none', p: 0, width: 32, height: 32, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', cursor: 'pointer', '&:active': { backgroundColor: 'background.subtle' } }}>
                            <ProtoIcon name="edit" size={16} />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {visibleKpis.map((k) => <Kpi key={k.id} {...k} />)}
                    </Box>
                </>
            )}

            {layout.sections.map((id) => <React.Fragment key={id}>{renderSection(id)}</React.Fragment>)}

            <MobileSheet
                open={kpiEditOpen}
                onClose={() => setKpiEditOpen(false)}
                title="Indicadores do resumo"
                footer={(
                    <>
                        <KitButton variant="default" block onClick={() => setKpiDraft(resolveSelection(KPI_DEFAULT).length ? resolveSelection(KPI_DEFAULT) : availableIds.slice(0, KPI_MAX))}>Restaurar padrão</KitButton>
                        <KitButton variant="primary" block onClick={saveKpis}>Salvar</KitButton>
                    </>
                )}
            >
                <Typography sx={{ fontSize: 12.5, color: kpiDraft.length >= KPI_MAX ? 'warning.main' : 'text.disabled', fontWeight: kpiDraft.length >= KPI_MAX ? 700 : 400, pb: '4px' }}>
                    {kpiDraft.length >= KPI_MAX
                        ? `Limite de ${KPI_MAX} indicadores atingido. Desative um para escolher outro.`
                        : `Escolha de ${KPI_MIN} a ${KPI_MAX} indicadores (${kpiDraft.length}/${KPI_MAX}).`}
                </Typography>
                {kpis.map((k) => {
                    const checked = kpiDraft.includes(k.id);
                    return (
                        <KitSwitch
                            key={k.id}
                            title={k.label}
                            description={KPI_DESCRIPTIONS[k.id]}
                            checked={checked}
                            disabled={checked ? kpiDraft.length <= KPI_MIN : kpiDraft.length >= KPI_MAX}
                            onChange={() => toggleKpi(k.id)}
                        />
                    );
                })}
            </MobileSheet>

            <MobileSheet
                open={Boolean(layoutOpen)}
                onClose={onLayoutClose}
                title="Personalizar início"
                footer={(
                    <>
                        <KitButton variant="default" block onClick={() => setLayoutDraft(defaultLayout())}>Restaurar padrão</KitButton>
                        <KitButton variant="primary" block onClick={saveLayout}>Salvar</KitButton>
                    </>
                )}
            >
                {availableHeroes.length > 0 && (
                    <>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, pb: '8px' }}>Card principal</Typography>
                        <KitSeg options={availableHeroes} value={layoutDraft.hero} onChange={(hero) => setLayoutDraft((prev) => ({ ...prev, hero }))} />
                    </>
                )}
                {availableSections.length > 0 && (
                    <>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, pt: '18px' }}>Seções</Typography>
                        <Typography sx={{ fontSize: 12.5, color: layoutDraft.sections.length >= SECTION_MAX ? 'warning.main' : 'text.disabled', fontWeight: layoutDraft.sections.length >= SECTION_MAX ? 700 : 400, pb: '4px' }}>
                            {layoutDraft.sections.length >= SECTION_MAX
                                ? `Limite de ${SECTION_MAX} seções atingido. Desative uma para escolher outra.`
                                : `Escolha de ${SECTION_MIN} a ${SECTION_MAX} seções (${layoutDraft.sections.length}/${SECTION_MAX}). Aparecem na ordem em que você ativar.`}
                        </Typography>
                        {availableSections.map((s) => {
                            const checked = layoutDraft.sections.includes(s.id);
                            return (
                                <KitSwitch
                                    key={s.id}
                                    title={s.label}
                                    description={s.description}
                                    checked={checked}
                                    disabled={checked ? layoutDraft.sections.length <= SECTION_MIN : layoutDraft.sections.length >= SECTION_MAX}
                                    onChange={() => toggleSection(s.id)}
                                />
                            );
                        })}
                    </>
                )}
            </MobileSheet>

            <MobileSheet open={notifOpen} onClose={onNotifClose} title="Notificações">
                {notifications.length > 0 ? (
                    <KitList>
                        {notifications.map((n) => (
                            <KitItem
                                key={n.id}
                                icon={n.id === 'late' ? Alert : n.id === 'stock' ? Package : Receipt}
                                tone={n.tone}
                                title={n.title}
                                sub={n.sub}
                                chevron
                                onClick={() => { onNotifClose(); navigate(n.path); }}
                            />
                        ))}
                    </KitList>
                ) : (
                    <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', py: '20px' }}>Sem notificações</Typography>
                )}
            </MobileSheet>

            <Dialog open={generateOpen} onClose={() => { if (!generating) setGenerateOpen(false); }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: '17px', fontWeight: 800 }}>Gerar ordem de serviço</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mb: 1 }}>
                        Será criada uma ordem de serviço com o serviço abaixo. Confirma?
                    </Typography>
                    {upcoming.item && (
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>
                            {[upcoming.item.service_name, [upcoming.item.vehicle_model, upcoming.item.license_plate].filter(Boolean).join(' '), upcoming.item.customer_name].filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                    {generateError && (
                        <Typography sx={{ fontSize: '13px', color: 'error.main', mt: 1.5 }}>{generateError}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setGenerateOpen(false)} disabled={generating}>Cancelar</Button>
                    <Button variant="contained" onClick={handleGenerate} disabled={generating}>
                        {generating ? 'Gerando…' : 'Gerar OS'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
